import intersectionWith from "lodash/intersectionWith";
import differenceWith from "lodash/differenceWith";
import { selectCompareSpecs } from "../../../../redux/slices/CarConfigCompareSpecsSlice";
import { useCarConfigSelector } from "../../../../redux/store";
import type { AtmosphereType } from "../../../../utils/types/AtmosphereType";
import type { CompareSpecsGradeEquipmentType, CompareSpecsGradeType } from "../../../../utils/types/CompareSpecsType";
import { useCompareContext } from "./CompareContext";
import { getDetailedGrade } from "./compareUtils";
import { useAphLbxAtmospheres } from "../../../../hooks/lbx/useAphLbxAtmospheres";

export const useGetCompareGrades = (): AtmosphereType[] => {
    const allGrades = useAphLbxAtmospheres();
    const compareContext = useCompareContext();
    const grade1 = allGrades.find(({ id }) => id === compareContext.grades[0]);
    const grade2 = allGrades.find(({ id }) => id === compareContext.grades[1]);
    return [grade1, grade2].filter(Boolean) as AtmosphereType[];
};

export type useGetCompareGradesReturnType = ReturnType<typeof useGetCompareGrades>;

export const useDetailedGrade = (grade?: AtmosphereType): CompareSpecsGradeType | undefined => {
    const compareSpecsGrades = useCarConfigSelector(selectCompareSpecs);
    return getDetailedGrade(compareSpecsGrades, grade);
};

export const useSharedAndUniqueFeatures = (): {
    shared: CompareSpecsGradeEquipmentType[];
    unique: [CompareSpecsGradeEquipmentType[], CompareSpecsGradeEquipmentType[]];
} => {
    const grades = useGetCompareGrades();
    const detailedGrade1 = useDetailedGrade(grades[0]);
    const detailedGrade2 = useDetailedGrade(grades[1]);

    if (!detailedGrade1 || !detailedGrade2) {
        return {
            shared: [],
            unique: [[], []],
        };
    }

    const sharedFeatures = intersectionWith(
        detailedGrade1.equipment,
        detailedGrade2.equipment,
        ({ id: id1 }, { id: id2 }) => id1 === id2,
    );

    const uniqueFeatures1 = differenceWith(
        detailedGrade1.equipment,
        detailedGrade2.equipment,
        ({ id: id1 }, { id: id2 }) => id1 === id2,
    );
    const uniqueFeatures2 = differenceWith(
        detailedGrade2.equipment,
        detailedGrade1.equipment,
        ({ id: id1 }, { id: id2 }) => id1 === id2,
    );

    return {
        shared: sharedFeatures,
        unique: [uniqueFeatures1, uniqueFeatures2],
    };
};
